import { z } from "zod";
import { paginationQuery, searchParam } from "../pagination";
import { type CompetitorMemberRef, type CompetitorRef, clientIdField } from "./common";
import type { ShortlistRole } from "../shortlist";
import { COMPETITOR_STATUSES, MEMBER_ROLES, type Medal } from "./vocab";

/**
 * Competitors — whatever competes in ONE event: an athlete, or a team / pair /
 * relay / crew — and their rosters. `/events/:id/competitors`,
 * `/competitors/:id`, and the people picker `/events/:id/eligible-people`.
 *
 * There is no separate "team" anywhere. An individual is a competitor with one
 * ATHLETE member; a team is a competitor with a roster. The predecessor had a
 * team table beside its participants, and 5,231 of its 7,086 team rows ended
 * up with no team link at all.
 *
 * Every export here is prefixed (competitor…, eligiblePeople…): all the
 * competition modules are re-exported through one barrel, and two modules
 * exporting the same name is a compile error there.
 */

// ─── Limits ──────────────────────────────────────────────────────────────────
//
// One copy, read by the API's validators and by any form (CLAUDE.md, bug #4).

/** A football squad with its reserves fits; nothing that competes is larger. */
export const COMPETITOR_MEMBERS_MAX = 60;
/** `POST /events/:id/competitors/bulk` — a whole start field in one call. */
export const COMPETITORS_BULK_MAX = 200;
export const COMPETITOR_TEAM_NAME_MAX = 200;
/** "48.12", "6.05", "2:01:33" — the mark submitted with the entry, as text. */
export const COMPETITOR_ENTRY_MARK_MAX = 50;
export const COMPETITOR_BIB_MAX = 20;
/** The people picker without an organization must be narrowed by a name of at least this many characters. */
export const ELIGIBLE_PEOPLE_SEARCH_MIN = 2;
/**
 * M7a `pick=support` (guides, pilots, coxes): the search is required and
 * matched through a trigram index, which needs three letters — a two-letter
 * search over every card holder cost 90–115 ms at 60k cards.
 */
export const ELIGIBLE_PEOPLE_SUPPORT_SEARCH_MIN = 3;
/** The audit headline for a change. Same ceiling as the API's own `reasonField` (pinned by the parity test). */
export const COMPETITOR_REASON_MAX = 300;

/** `entry_no`, `seed` and `sort_order` are `smallint` columns: a larger number is a 422 here, not a Postgres error. */
const SMALLINT_MAX = 32_767;

// ─── Fields ──────────────────────────────────────────────────────────────────
//
// Strings trim FIRST, then check length: zod 3 runs checks in order, so
// `.min(1).trim()` would let "   " through and save it as "" (bug #5).

/** Lower-cased like `clientIdField`, so "the same person twice" is caught whatever case the client sent. */
const idField = z.string().uuid().toLowerCase();
const teamNameField = z.string().trim().min(1).max(COMPETITOR_TEAM_NAME_MAX);
const entryMarkField = z.string().trim().min(1).max(COMPETITOR_ENTRY_MARK_MAX);
const bibField = z.string().trim().min(1).max(COMPETITOR_BIB_MAX);
/** 1, 2, 3 — one organization's entries in the same event (Malaysia 1, Malaysia 2). */
const entryNoField = z.number().int().min(1).max(SMALLINT_MAX);
const seedField = z.number().int().min(1).max(SMALLINT_MAX);
const sortOrderField = z.number().int().min(0).max(SMALLINT_MAX);

/** Adds an issue on every element after the first that repeats `key` — "this person is listed twice". */
function flagRepeats<T>(
	items: readonly T[],
	key: (item: T) => string | undefined,
	path: (index: number) => (string | number)[],
	message: string,
	ctx: z.RefinementCtx,
): void {
	const seen = new Set<string>();
	items.forEach((item, index) => {
		const value = key(item);
		if (value === undefined) return;
		if (seen.has(value)) ctx.addIssue({ code: z.ZodIssueCode.custom, path: path(index), message });
		seen.add(value);
	});
}

// ─── Requests ────────────────────────────────────────────────────────────────

/**
 * One person on a roster.
 *
 * The person IS the member's identity within the competitor: a member row
 * never changes person (that would rewrite who played a meet). On a replace
 * (`PUT /competitors/:id/members`), someone already on the roster keeps their
 * row and `competitor_member_id`; the id sent here is used only when the
 * person is ADDED.
 *
 * Omitted fields: `role` ATHLETE, `is_captain` false, `sort_order` the
 * member's position in the list (so the order sent is the order shown).
 * `sport_class_id` must be one of the event's own classes.
 */
export const competitorMemberRequest = z.object({
	competitor_member_id: clientIdField,
	people_id: idField,
	role: z.enum(MEMBER_ROLES).optional(),
	sport_class_id: idField.optional(),
	bib: bibField.optional(),
	is_captain: z.boolean().optional(),
	sort_order: sortOrderField.optional(),
});
export type CompetitorMemberBody = z.input<typeof competitorMemberRequest>;

const rosterField = z
	.array(competitorMemberRequest)
	.min(1)
	.max(COMPETITOR_MEMBERS_MAX)
	.superRefine((members, ctx) => {
		flagRepeats(members, (m) => m.people_id, (i) => [i, "people_id"], "This person is listed twice", ctx);
		flagRepeats(members, (m) => m.competitor_member_id, (i) => [i, "competitor_member_id"], "This member id is listed twice", ctx);
	});

/**
 * `POST /events/:id/competitors` — one competitor and its whole roster, in
 * one transaction.
 *
 * - `competitor_id` (client-made) makes the call repeatable: sending it again
 *   returns the existing competitor (200) and writes nothing.
 * - `entry_no` omitted = that organization's highest in the event + 1.
 * - `team_name` is for teams; an INDIVIDUAL event refuses it. The display
 *   name of an unnamed competitor is built by the API (`CompetitorRef.name`).
 * - The organization is who the entry competes FOR, and it never changes:
 *   entered for the wrong one is delete and re-create.
 */
export const createCompetitorRequest = z.object({
	competitor_id: clientIdField,
	responsible_organization_id: idField,
	entry_no: entryNoField.optional(),
	team_name: teamNameField.optional(),
	seed: seedField.optional(),
	entry_mark: entryMarkField.optional(),
	bib: bibField.optional(),
	members: rosterField,
});
export type CreateCompetitorBody = z.input<typeof createCompetitorRequest>;

/**
 * `POST /events/:id/competitors/bulk` — a whole start field at once,
 * all-or-nothing, same rules as one create. A person may appear in only one
 * competitor of the batch: one person, one competitor per event.
 */
export const bulkCreateCompetitorsRequest = z.object({
	competitors: z
		.array(createCompetitorRequest)
		.min(1)
		.max(COMPETITORS_BULK_MAX)
		.superRefine((list, ctx) => {
			flagRepeats(list, (c) => c.competitor_id, (i) => [i, "competitor_id"], "This competitor id is listed twice", ctx);
			const people = list.flatMap((c, i) => c.members.map((m, j) => ({ key: m.people_id, at: [i, "members", j, "people_id"] })));
			flagRepeats(people, (p) => p.key, (k) => people[k]!.at, "This person is in two competitors of the batch", ctx);
			const memberIds = list.flatMap((c, i) => c.members.map((m, j) => ({ key: m.competitor_member_id, at: [i, "members", j, "competitor_member_id"] })));
			flagRepeats(memberIds, (m) => m.key, (k) => memberIds[k]!.at, "This member id is listed twice", ctx);
		}),
});
export type BulkCreateCompetitorsBody = z.input<typeof bulkCreateCompetitorsRequest>;

/**
 * `PATCH /competitors/:id`. `null` clears an optional field; an omitted one is
 * left alone. The organization is not here on purpose (see the create).
 * Setting `status` back to ENTERED counts against `max_entries_per_noc` again.
 */
export const updateCompetitorRequest = z
	.object({
		entry_no: entryNoField.optional(),
		team_name: teamNameField.nullable().optional(),
		seed: seedField.nullable().optional(),
		entry_mark: entryMarkField.nullable().optional(),
		bib: bibField.nullable().optional(),
		status: z.enum(COMPETITOR_STATUSES).optional(),
		/** Headline for this change in the Activity feed. */
		reason: z.string().trim().min(1).max(COMPETITOR_REASON_MAX).optional(),
	})
	.refine(({ reason: _reason, ...fields }) => Object.values(fields).some((v) => v !== undefined), "At least one field must be provided");
export type UpdateCompetitorBody = z.input<typeof updateCompetitorRequest>;

/**
 * `PUT /competitors/:id/members` — the whole roster, replaced. Listed people
 * are kept (and updated) or added; everyone else is removed — except a member
 * who has already been lined up for a meet, which refuses the whole call.
 */
export const replaceCompetitorMembersRequest = z.object({ members: rosterField });
export type ReplaceCompetitorMembersBody = z.input<typeof replaceCompetitorMembersRequest>;

/**
 * `PATCH /competitors/:id/members` — shirt numbers (bibs) only, for the
 * members listed; everyone else is left alone. Not the replace-all PUT: a
 * screen that only renumbers shirts must not resend (and so be able to drop)
 * people, roles or sport classes it never showed.
 *
 * `bib: null` clears the number. A number another member of the roster
 * would still wear after the change is refused (409 `{ field: "bib", bibs }`);
 * an id that is not on this roster is refused (409 `{ field: "members",
 * competitor_member_ids }`).
 */
export const updateCompetitorMemberBibsRequest = z
	.object({
		members: z
			.array(z.object({ competitor_member_id: idField, bib: bibField.nullable() }).strict())
			.min(1)
			.max(COMPETITOR_MEMBERS_MAX)
			.superRefine((members, ctx) => {
				flagRepeats(members, (m) => m.competitor_member_id, (i) => [i, "competitor_member_id"], "This member is listed twice", ctx);
			}),
	})
	.strict();
export type UpdateCompetitorMemberBibsBody = z.input<typeof updateCompetitorMemberBibsRequest>;

/** `details` of the 409 when a shirt number would be worn twice in one roster. */
export interface CompetitorBibConflictDetails {
	field: "bib";
	/** The numbers worn twice, in order. */
	bibs: string[];
}

/**
 * `GET /events/:id/competitors`. `search` matches the team name, a member's
 * name, the organization's name and its NOC code. Ordered by NOC code, then
 * `entry_no`.
 */
export const listEventCompetitorsQuery = paginationQuery.extend({
	responsible_organization_id: idField.optional(),
	status: z.enum(COMPETITOR_STATUSES).optional(),
	search: searchParam.optional(),
});
export type ListEventCompetitorsQuery = z.infer<typeof listEventCompetitorsQuery>;

/**
 * What the picker lists (M7a):
 *   shortlisted  people shortlisted for the event (PRIMARY / RESERVE, APPROVED
 *                card) — the only ones who may be an ATHLETE or RESERVE member;
 *   support      anyone holding a live card the caller may see (the pre-M7a
 *                rule) — for a GUIDE, PILOT or COX, who need no shortlist.
 *                Always narrowed by a printed-name search (≥
 *                ELIGIBLE_PEOPLE_SUPPORT_SEARCH_MIN letters): browsing a large
 *                NOC's every card holder by name is not a picker (Rule 0).
 */
export const ELIGIBLE_PEOPLE_PICKS = ["shortlisted", "support"] as const;
export type EligiblePeoplePick = (typeof ELIGIBLE_PEOPLE_PICKS)[number];

/**
 * `GET /events/:id/eligible-people` — the picker behind "add athlete": people
 * not yet on any roster of this event, `pick` deciding who (default
 * shortlisted). Without an organization it must be
 * narrowed by a name: browsing every person is not a picker.
 */
export const eligiblePeopleQuery = paginationQuery
	.extend({
		responsible_organization_id: idField.optional(),
		search: searchParam.optional(),
		pick: z.enum(ELIGIBLE_PEOPLE_PICKS).default("shortlisted"),
	})
	.superRefine((q, ctx) => {
		if (q.pick === "support" && (q.search?.length ?? 0) < ELIGIBLE_PEOPLE_SUPPORT_SEARCH_MIN) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["search"],
				message: `Type at least ${ELIGIBLE_PEOPLE_SUPPORT_SEARCH_MIN} letters of a name to find a guide, pilot or cox`,
			});
		} else if (q.responsible_organization_id === undefined && (q.search?.length ?? 0) < ELIGIBLE_PEOPLE_SEARCH_MIN) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["search"],
				message: `Type at least ${ELIGIBLE_PEOPLE_SEARCH_MIN} letters of a name, or choose an organization`,
			});
		}
	});
export type EligiblePeopleQuery = z.infer<typeof eligiblePeopleQuery>;

// ─── Responses ───────────────────────────────────────────────────────────────

/**
 * One competitor, as every competitor endpoint returns it. `CompetitorRef`'s
 * fields (`competitor_id`, `entry_no`, `name`, `status`, `noc`) come first, so
 * a client that understands the shared shape understands this one.
 *
 * `name` is always the DISPLAY name; `team_name` is the stored team name,
 * null for an individual or an unnamed team. `final_rank` and
 * `proposed_medal` are OVR's proposal — never a medal (only `event_medals`
 * is). `meets_count` is the number of meet slots this competitor holds.
 */
export interface CompetitorItem extends CompetitorRef {
	event_id: string;
	team_name: string | null;
	seed: number | null;
	entry_mark: string | null;
	bib: string | null;
	final_rank: number | null;
	proposed_medal: Medal | null;
	/** The roster, in `sort_order`. */
	members: CompetitorMemberRef[];
	meets_count: number;
	/**
	 * 0073: an INDIVIDUAL entry's athlete's shortlist role for this event under
	 * the entry's organisation (APPROVED card) — a shortlisted athlete IS the
	 * entry, so RESERVE marks a reserve. Null for a team, or when not shortlisted.
	 */
	shortlist_role: ShortlistRole | null;
	created_at: string;
	updated_at: string;
}

/** `POST /events/:id/competitors/bulk`. `existing` = ids sent again, which wrote nothing. */
export interface CompetitorBulkResult {
	created: string[];
	existing: string[];
}

export interface EligiblePersonOrganization {
	responsible_organization_id: string;
	name: string;
	/** M7a: how this organisation shortlisted the person for the event; null = not shortlisted by it. */
	shortlist_role: ShortlistRole | null;
}

/**
 * One row of the people picker. An allow-list: no identity numbers, contacts
 * or dates of birth. M7a, `pick=shortlisted` (default): only people
 * SHORTLISTED for this event on an APPROVED card the caller may see, and
 * `organizations` are the organisations that shortlisted them; an ATHLETE or
 * RESERVE member must be one of these (422 NOT_SHORTLISTED). `pick=support`:
 * anyone with a live card the caller may see, `organizations` = those cards'
 * — for a GUIDE, PILOT or COX.
 */
export interface EligiblePerson {
	people_id: string;
	name: string;
	gender: string | null;
	/** Nationality — ISO 3166 alpha-3. */
	country_code: string;
	/** M7a: PRIMARY when any of `organizations` has them as PRIMARY, else RESERVE; null = not shortlisted (support roles only). The picker's badge. */
	shortlist_role: ShortlistRole | null;
	organizations: EligiblePersonOrganization[];
}
